import React, { useEffect, useMemo } from 'react';
import { OpDataGrid } from 'op2mise-react-widgets/OpDataGrid';
import { addButtonIcon, deleteIcon, pencilIcon } from 'assets';

function BreakPatternEditorTable({
  breakChannelTableError,
  handleAddBreakType,
  handleEditBreakType,
  rowData,
  setDirtyForm,
  setSortingResult,
  setPatternData,
}) {
  const handleDeleteBreakType = (params) => {
    setDirtyForm(true);
    setPatternData((prevData) => {
      const updatedRows = prevData.breakChannelTypes.filter((row) => row.id !== params.data.id);
      return {
        ...prevData,
        breakChannelTypes: updatedRows,
      };
    });
  };

  const renderOptionalCell = (params) => (params.value ? 'Yes' : 'No');

  // TODO: Formally turn these img elements into buttons with proper accessibility
  // tags and keyboard support
  const actionCellButtons = (params) => (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100%',
        width: '100%',
        gap: '12px',
      }}
    >
      <img
        src={addButtonIcon}
        alt="Add new row icon"
        title="Add new row"
        onClick={handleAddBreakType}
        style={{
          cursor: 'pointer',
          transform: 'rotate(-45deg)',
          height: '22px',
        }}
      />
      <img
        onClick={() => handleEditBreakType(params)}
        src={pencilIcon}
        style={{
          cursor: 'pointer',
        }}
        title="Edit"
        alt="Edit"
      />
      <img
        src={deleteIcon}
        alt="Delete Icon"
        title="Delete this"
        onClick={() => handleDeleteBreakType(params)}
        style={{
          cursor: 'pointer',
        }}
      />
    </div>
  );

  const columnDefinitions = useMemo(
    () => [
      {
        field: 'type',
        headerName: 'Type',
      },
      {
        field: 'slotPosition',
        headerName: 'Segment Position',
      },
      {
        field: 'optional',
        headerName: 'Optional',
        tooltipValueGetter: renderOptionalCell,
        valueFormatter: renderOptionalCell,
        filterParams: {
          valueFormatter: renderOptionalCell,
        },
      },
      {
        field: 'position',
        headerName: 'Position',
      },
      {
        field: 'selection',
        headerName: 'Selection',
      },
      {
        field: 'externalID',
        headerName: 'External ID',
      },
      {
        field: 'eventName',
        headerName: 'Event',
      },
      {
        field: 'priority',
        headerName: 'Priority',
      },
      {
        field: 'action',
        headerName: ' ',
        cellRenderer: actionCellButtons,
        editable: false,
        width: 100,
      },
    ],
    [actionCellButtons],
  );

  // Returns all fields from columnDefinition except the action column
  const headerFields = columnDefinitions
    .filter((column) => column.field !== 'action')
    .map((column) => column.field);

  useEffect(() => {
    setSortingResult((prevState) => ({
      ...prevState,
      ...Object.fromEntries(headerFields.map((field) => [field, false])),
    }));
  }, []);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      <div style={{ flex: 1, minHeight: 0 }}>
        <OpDataGrid
          skipGridLoading // WORKAROUND: prevents spinner flash on every cell edit; see BaseGrid updateRowData
          columns={columnDefinitions}
          customVerbiage="No break patterns found"
          enableMultipleRowSelection={false}
          enableMultipleColumnSorting
          filter
          gridHeightPreset={{ height: 'calc(100% - 60px)', minHeight: '0' }}
          gridName="break-patterns-editor"
          resizable
          rows={rowData}
          sortable
          sharedColumnProperties={{
            cellStyle: { fields: headerFields, value: { textAlign: 'left' } },
            flex: { fields: headerFields, value: 1 },
          }}
          showAlternativeRowHighlight
        />
      </div>

      {breakChannelTableError && (
        <div style={{ display: 'flex', justifyContent: 'end' }}>
          <small className="error-message">{breakChannelTableError}</small>
        </div>
      )}
    </div>
  );
}

export default BreakPatternEditorTable;
